import { useEffect, useRef, useState } from 'react'
import { storage } from '../api'
import { useI18n, type MessageKey } from '../i18n'
import type { MediaInfo } from '../protocol'
import { Popover } from '../ui/Popover'

const HINT_KEY = 'chillio.hdrHintSeen'

/** Whether the screen currently shows HDR; follows the system switch live. */
export function useHdrScreen(): boolean {
  const [hdr, setHdr] = useState(() => matchMedia('(dynamic-range: high)').matches)
  useEffect(() => {
    const q = matchMedia('(dynamic-range: high)')
    const on = () => setHdr(q.matches)
    q.addEventListener('change', on)
    return () => q.removeEventListener('change', on)
  }, [])
  return hdr
}

type Platform = 'windows' | 'mac' | 'other'

function platform(): Platform {
  const p = ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform).toLowerCase()
  if (p.includes('win')) return 'windows'
  if (p.includes('mac')) return 'mac'
  return 'other'
}

/**
 * What the viewer gets from an HDR video:
 *  - hdr: shown in HDR;
 *  - screen-off: the screen or the system shows SDR, so the browser tone maps;
 *  - transcoded: the browser cannot decode the codec and gets an SDR copy;
 *  - dv-only: Dolby Vision profile 5, whose colors come out wrong without
 *    Dolby Vision support.
 */
export type HdrState = 'hdr' | 'screen-off' | 'transcoded' | 'dv-only'

export function hdrState(media: MediaInfo, deliveredCodec: string, screenHdr: boolean): HdrState | null {
  const v = media.video
  if (!v.range) return null
  if (v.dolbyVision && !v.dvCompat) return 'dv-only'
  // The server sends H.264 only when it transcodes.
  if (v.codec !== 'h264' && deliveredCodec.startsWith('avc1')) return 'transcoded'
  return screenHdr ? 'hdr' : 'screen-off'
}

function label(media: MediaInfo): string {
  const v = media.video
  if (v.dolbyVision) return `Dolby Vision ${v.dolbyVision}`
  return v.range === 'hlg' ? 'HLG' : 'HDR'
}

const TEXT: Record<HdrState, MessageKey> = {
  hdr: 'hdr.on',
  'screen-off': 'hdr.screenOff',
  transcoded: 'hdr.transcoded',
  'dv-only': 'hdr.dvOnly',
}

function HowTo() {
  const { t } = useI18n()
  const p = platform()
  return <p className="hdr-pop__how">{t(p === 'windows' ? 'hdr.howWindows' : p === 'mac' ? 'hdr.howMac' : 'hdr.howOther')}</p>
}

/** The HDR badge in the player controls with an explanation on click. */
export function HdrBadge({ media, state }: { media: MediaInfo; state: HdrState }) {
  const { t } = useI18n()
  const btn = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`hdr-badge hdr-badge--${state}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t('hdr.aria', { name: label(media) })}
      >
        {label(media)}
        {state !== 'hdr' && <span className="hdr-badge__warn" aria-hidden="true" />}
      </button>
      <Popover anchor={btn} open={open} onClose={() => setOpen(false)} label={label(media)}>
        <div className="hdr-pop">
          <p className="hdr-pop__title">{label(media)}</p>
          <p>{t(TEXT[state])}</p>
          {state === 'screen-off' && <HowTo />}
        </div>
      </Popover>
    </>
  )
}

/**
 * A one-time hint when an HDR film plays on a screen with HDR off: that
 * switch belongs to the system, browsers cannot flip it.
 */
export function HdrHint({ state }: { state: HdrState | null }) {
  const { t } = useI18n()
  const [seen, setSeen] = useState(() => storage.get(HINT_KEY) === '1')
  if (seen || state !== 'screen-off') return null
  const dismiss = () => {
    storage.set(HINT_KEY, '1')
    setSeen(true)
  }
  return (
    <div className="hdr-hint" role="status">
      <span className="hdr-hint__badge">HDR</span>
      <div className="hdr-hint__text">
        <p>{t('hdr.hintTitle')}</p>
        <HowTo />
      </div>
      <button type="button" className="hdr-hint__close" onClick={dismiss} aria-label={t('hdr.dismiss')}>
        ×
      </button>
    </div>
  )
}
